import React, { useState } from 'react'
import styled from 'styled-components'
import Helmet from 'react-helmet'
import favicon from '../images/favicon.ico'
import { Arrow } from '../assets/svg/criticalSVG'
import { Link } from 'gatsby'
import { navigate } from 'gatsby'
import blogCategories from '../../content/blogCategories.json'
import BlogCategoryList from '../components/BlogCategoryList'
import blogListings from '../../content/blogListings.json'
import BlogPostCard from '../components/BlogPostCard'
import Breadcrumbs from '../components/Breadcrumbs'
import Button from '../components/Button'
import Container from '../Layout/Container'
import CTA from '../sections/CTA'
import Form from '../components/Form'
import FormSection from '../sections/Form'
import Layout from '../Layout/Layout'
import ReactPaginate from 'react-paginate'
import SearchContext from '../context/SearchContext'
import SEO from '../components/seo'
import TextHeader from '../components/TextHeader'



const StyledBlog = styled.div`
  padding: 240px 24px 0 24px;

  ${({ theme }) => theme.media.desktop} {
    padding-left: 0;
    padding-right: 0;
  }
`

const StyledHeader = styled.h1`
  align-items: flex-end;
  display: flex;
  font-size: 24px;
  font-weight: 400;
  justify-content: space-between;
  line-height: 1.5;
  margin-bottom: 32px;
  min-height: 36px;
`

const StyledRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 64px;
  padding-bottom: 64px;
  width: 100%;

  ${({ theme }) => theme.media.desktop} {
    flex-direction: row;
    margin-bottom: 64px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const StyledColumn = styled.div`
  display: block;
  margin-bottom: 24px;
  width: 100%;

  ${({ theme }) => theme.media.desktop} {
    margin-bottom: 0;
    width: ${({ width }) => width}%;
  }
`

const BlogGrid = styled.div`
  display: grid;
  grid-gap: 24px;
  grid-template-columns: 1fr;
  width: 100%;

  ${({ theme }) => theme.media.desktop} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const FeaturedPopularGrid = styled.div`
  display: grid;
  grid-gap: 24px;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;

  ${({ theme }) => theme.media.desktop} {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 36px repeat(3, 186px);
  }
`

const StyledFeatured = styled.div`
  grid-area: 2 / 1 / span 3 / span 1;

  article {
    height: 100%;

    section {
      min-height: 364px;
    }

    main {
      height: initial;
      min-height: 244px;

      p {
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
      }
    }
  }
`

const StyledPagination = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  padding-bottom: 88px;

  ${({ theme }) => theme.media.desktop} {
    flex-direction: row;
    justify-content: space-between;
  }

  ul {
    display: flex;

    li {
      &.previous {
        transform: rotate(90deg);
      }

      &.next {
        transform: rotate(-90deg);
      }

      a {
        color: #8a94a6;
        font-size: 15px;
        line-height: 1.67;
        padding: 0 4px;

        &[aria-current] {
          color: #4e5d78;
          font-weight: 500;
        }
      }
    }
  }
`

const StyledPaginationInfo = styled.p`
  color: #8a94a6;
  font-size: 15px;
  line-height: 1.67;
`

const StyledViewAll = styled(Link)`
  color: #8a94a6;
  font-size: 14px;
  line-height: 24px;

  svg {
    transform: rotate(-90deg);
  }
`

const StyledBlogForm = styled.div`
  grid-area: 2/1/2/2;

  ${({ theme }) => theme.media.desktop} {
    grid-area: 1/2/2/4;
  }
`

const StyledCard = styled.div`
  align-items: flex-end;
  border-radius: 8px;
  height: 100%;
  overflow: hidden;

  ${({ theme }) => theme.media.desktop} {
    display: block;
    margin-bottom: 0;
    width: 100%;
  }

  .gatsby-image-wrapper {
    border-radius: 8px;
    height: 100%;
    overflow: hidden;
    width: 100%;
  }
`

const StyledRedCard = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.red};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 500px;
  position: relative;
  width: 100%;

  h1, a {
    z-index: 2000;
  }

  a {
    background-color: #fff;
    color: ${({ theme }) => theme.colors.red};
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 1px;
    line-height: 24px;
    padding: 12px 48px;
    text-transform: uppercase;
  }
`

const StyledCardTitle = styled.h1`
  color: #fff;
  font-size: 1.75em;
  font-weight: 500;
  line-height: 40px;
  margin-bottom: 24px;
  text-align: center;
  width: 80%;
`

const StyledCardParagraph = styled.p`
  color: #fff;
  font-size: 14px;
  line-height: 24px;
  margin-bottom: 24px;
  padding: 0 33px;
  text-align: center;
`

const Pagination = ({ pageContext, totalItems }) => {
  let paginationItems = []

  for (let i = 0; i < pageContext.numberOfPages; i++) {
    paginationItems.push(i + 1)
  }

  return (
    <StyledPagination>
      <ReactPaginate
        pageCount={pageContext.numberOfPages}
        pageRangeDisplayed={6}
        marginPagesDisplayed={2}
        initialPage={pageContext.pageNumber}
        hrefBuilder={page => (page === 1 ? `/blog` : `/blog/page/${page}`)}
        onPageChange={page =>
          page.selected === 0
            ? navigate(`/blog`)
            : navigate(`/blog/page/${page.selected + 1}`)
        }
        disableInitialCallback={true}
        previousLabel={<Arrow />}
        nextLabel={<Arrow />}
      />
      <StyledPaginationInfo>
        {pageContext.skip + 1} -{' '}
        {Math.min(pageContext.skip + pageContext.limit, totalItems)} out of{' '}
        {totalItems} items
      </StyledPaginationInfo>
    </StyledPagination>
  )
}

const Blog = ({ pageContext }) => {
  const [searchMode, turnSearchModeOn] = useState(false)
  const [searchResults, setSearchResults] = useState([])

  const { limit, pageNumber } = pageContext
  // Filter/Reduce to a dict as there may be duplicates
  const allPosts = Object.values(
    blogListings.reduce((acc, item) => {
      if (item.isBlog) {
        acc[item.id] = item
      }
      return acc
    }, {})
  )
  const posts = allPosts.slice(pageNumber * limit, (pageNumber + 1) * limit)
  const categories = blogCategories
  const featured = allPosts.filter(item => item.featured)
  const popular = allPosts.filter(item => item.popular)
  const randomFeatured = featured[Math.floor(Math.random() * featured.length)]
  const FormData = {
    confirmMessage: `#### Awesome, great content coming to your inbox very ' +
      'soon!\nWe just shot you an email to verify you’re really you.\nJust ' +
      'click the confirmation link and you’ll be all set.`,
    items: [
      {
        fields: {
          name: 'Embed Form',
          content: '<iframe loading="lazy" src="https://hello.learn.mailgun.com/l/830283/2019-12-19/bw8" width="100%" height="260" type="text/html" frameborder="0" allowTransparency="true" style="border: 0"></iframe>',
        },
      },
    ],
    name: 'Blog Pages Form',
    buttonText: 'Count Me In!',
    formType: 'blog-pages-form'
  }
  
  const FormSectionData = {
    centered: true,
    fullWidth: true,
    greyBG: true,
    name: 'Blog Pages Form',
    sectionType: 'Form',
    items: [
      {
        fields: {
          text:
            'No spam, ever. Only musings and writings from the Mailgun team.',
          title: 'Always be in the know and grab free email resources!',
          themeSize: 'tiny',
          themeAlign: 'center',
        },
        sys: {
          id: '1lEyOwdthmbQT8ALzdh7Ng',
          contentType: {
            sys: {
              id: 'textHeader',
            },
          },
        },
      },
      {
        fields: {
          formType: 'blog-pages-form'
        }
      },
    ],
  }

  return (
    <SearchContext.Provider
      value={{
        switchFn: turnSearchModeOn,
        handleResults: setSearchResults,
        engine: pageContext.options,
        posts: allPosts,
      }}
    >
      <SEO 
        title="Blog" 
        url="https://www.mailgun.com/blog/" 
        type="blog" 
      />
      <Helmet defer={false}>
        <title>Blog</title>
        <meta name="title" content={pageContext.seo.title} />
        <meta name="description" content={pageContext.seo.description} />
        <meta
          name="robots"
          content={pageContext.seo.robots || 'index,follow'}
        />
        {pageContext.pageNumber === 0 ? (
          <link rel="canonical" href="https://www.mailgun.com/blog/"/>
        ) : (
          <link rel="canonical" href={`https://www.mailgun.com/blog/page/${pageNumber + 1}/`} />
        )}
        <link rel="icon" href={favicon} />
      </Helmet>
      <Layout blog="true">
        <Container greyBG fullWidth>
          <Container>
            <StyledBlog>
              {searchMode && (
                <Breadcrumbs
                  items={[
                    { name: 'Blog', path: '/blog' },
                    {
                      name: 'Search',
                      path: `/blog`,
                    },
                    {
                      name: searchResults.query,
                      path: `/blog`,
                    },
                  ]}
                />
              )}
              {!searchMode && (
                <TextHeader
                  cmsData={{
                    title: allPosts[0].listingMd,
                    subtitleTop: allPosts[0].listingSubtitle,
                    themeSize: 'big',
                    themeAlign: 'center',
                  }}
                />
              )}
              <BlogCategoryList data={categories} />
              {pageContext.pageNumber === 0 && !searchMode && (
                <FeaturedPopularGrid>
                  <StyledHeader>Featured Story</StyledHeader>
                  <StyledFeatured>
                    <BlogPostCard data={randomFeatured} />
                  </StyledFeatured>
                  <StyledHeader>
                    Popular stories
                    <StyledViewAll to="/blog/category/popular">
                      View all <Arrow />
                    </StyledViewAll>
                  </StyledHeader>
                  {popular.slice(0, 3).map((popularPost, index) => (
                    <div key={index}>
                      <BlogPostCard wide="true" data={popularPost} />
                    </div>
                  ))}
                </FeaturedPopularGrid>
              )}
              <StyledRow>
                <StyledColumn width="100">
                  <StyledHeader>
                    {!searchMode
                      ? 'All stories'
                      : `Searching for "${searchResults.query}"`}
                  </StyledHeader>
                  <BlogGrid>
                    {pageContext.pageNumber === 0 && (
                      <StyledBlogForm>
                        <FormSection
                          blogListing={true}
                          noShift={true}
                          cmsData={FormSectionData}
                        >
                          <TextHeader
                            title={
                                <p className="blog-form-title">Always be in the know and grab free email
                                resources!</p>
                            }
                            text="No spam, ever. Only musings and writings from the Mailgun team."
                            theme={{
                              size: 'tiny',
                              align: 'center',
                              width: 'wide',
                            }}
                          />
                          <Form cmsData={FormData} />
                        </FormSection>
                      </StyledBlogForm>
                    )}
                    {!searchMode &&
                      posts.map(post => (
                        <BlogPostCard key={post.id} data={post} />
                      ))}
                    {searchMode ? (
                      searchResults.results.length > 0 ? (
                        searchResults.results.map(post => {
                          return <BlogPostCard key={post.id} data={post} />
                        })
                      ) : (
                        <div>No results found</div>
                      )
                    ) : null}
                    {pageContext.pageNumber === 0 && (
                      <StyledCard>
                        <StyledRedCard>
                          <StyledCardTitle>
                            Interested in joining the Mailgun Team?
                          </StyledCardTitle>
                          <StyledCardParagraph>
                            We’ve been rolling out a ton of new features these
                            past few weeks, and the rollout keeps coming with
                            our new email…
                          </StyledCardParagraph>
                          <Button xxl="true" to="/jobs">
                            See Open Positions
                          </Button>
                        </StyledRedCard>
                      </StyledCard>
                    )}
                  </BlogGrid>
                </StyledColumn>
              </StyledRow>
            </StyledBlog>
            {!searchMode && (
              <Pagination
                pageContext={pageContext}
                totalItems={allPosts.length}
              />
            )}
          </Container>
        </Container>
        <Container fullWidth>
          <CTA ghost slick>
            <TextHeader
              title={
                <div className="title">
                  It's easy to get started. <span>And it's free.</span>
                </div>
              }
              subtitle="sign up"
              text="See what you can accomplish with the world's best email delivery platform."
              theme={{
                size: 'big',
                align: 'center',
              }}
            />
            <Button
              primary="true"
              xxl="true"
              to="https://signup.mailgun.com/"
              external
              isAbsolutePath
            >
              Sign up for Free
            </Button>
          </CTA>
        </Container>
      </Layout>
    </SearchContext.Provider>
  )
}

export default Blog
